import React, { useState } from "react";
import { toast } from "sonner";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatErr } from "../../lib/api";
import { RES_STATUS } from "../../lib/testLibrary";

const VERDICT = {
  better: ["Mejora real", "#4ade80"], worse: ["Empeora", "#f87171"],
  within_error: ["Dentro del margen de error", "#a3a3a3"], no_change: ["Sin cambios", "#a3a3a3"], unqualified: ["Sin cambio mínimo definido: no se califica", "#737373"],
};
const fmt = (v, u) => (v === null || v === undefined ? "—" : `${v} ${u || ""}`.trim());
const Kpi = ({ l, v, sub, testid }) => (
  <div style={{ minWidth: 110 }} data-testid={testid}><div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#737373" }}>{l}</div>
    <div style={{ fontSize: 20, fontWeight: 800 }}>{v}</div>{sub && <div style={{ fontSize: 11, color: "#a3a3a3" }}>{sub}</div>}</div>
);

const Feedback = ({ r, onSaved }) => {
  const [c, setC] = useState("");
  const [vis, setVis] = useState("player");
  const send = (body) => api.post(`/admin/test-results/${r.id}/feedback`, body).then(() => { toast.success("Guardado"); setC(""); onSaved(); }).catch((e) => toast.error(formatErr(e)));
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
      <input className="qv-input" value={c} onChange={(e) => setC(e.target.value)} placeholder={vis === "internal" ? "Nota interna (no la ve el futbolista)" : "Comentario para el futbolista"} style={{ flex: 1, minWidth: 180, textTransform: "none", padding: "6px 8px", fontSize: 12 }} data-testid={`pp-comment-${r.id}`} />
      <select className="qv-input" value={vis} onChange={(e) => setVis(e.target.value)} style={{ width: "auto", padding: "6px 8px", fontSize: 12 }} data-testid={`pp-comment-vis-${r.id}`}><option value="player">Visible para el futbolista</option><option value="internal">Interno</option></select>
      <button className="qv-btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} disabled={!c.trim()} onClick={() => send({ comment: c, visibility: vis })} data-testid={`pp-comment-send-${r.id}`}>Enviar</button>
      {(r.video_file_ids?.length > 0 || r.video_url) && <button className="qv-btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} onClick={() => send({ video_reviewed: !r.video_reviewed })} data-testid={`pp-video-reviewed-${r.id}`}>{r.video_reviewed ? "Quitar vídeo revisado" : "Marcar vídeo revisado"}</button>}
    </div>
  );
};

const Chart = ({ p }) => {
  const t = p.template;
  const data = p.series.map((s) => ({ ...s, label: s.date }));
  const changes = data.filter((d, i) => i > 0 && d.version !== data[i - 1].version);
  const dom = t.measure_type === "score" || t.result_type === "score" ? [t.scale_min, t.scale_max] : ["auto", "auto"];
  return (
    <div style={{ height: 200, marginTop: 10 }} data-testid={`pp-chart-${t.id}`}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1f1f1f" />
          <XAxis dataKey="label" tick={{ fill: "#a3a3a3", fontSize: 11 }} />
          <YAxis domain={dom} tick={{ fill: "#a3a3a3", fontSize: 11 }} width={48} unit={data[0]?.unit ? ` ${data[0].unit}` : ""} />
          <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #333" }} formatter={(v, _n, it) => [`${v} ${it.payload.unit || "pts"}`, "Resultado"]} />
          {changes.map((c) => <ReferenceLine key={c.date} x={c.label} stroke="#facc15" strokeDasharray="4 4" label={{ value: "cambio de protocolo", fill: "#facc15", fontSize: 10 }} />)}
          <Line type="monotone" dataKey="value" stroke="#E11D2E" strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const TestCard = ({ p, admin, reload }) => {
  const t = p.template;
  const s = p.summary;
  const u = s?.unit || (t.result_type === "score" || t.measure_type === "score" ? "pts" : t.unit);
  const v = s?.verdict && VERDICT[s.verdict];
  return (
    <div className="qv-card-app" style={{ padding: 16, marginBottom: 12 }} data-testid={`pp-test-${t.id}`}>
      <div style={{ fontWeight: 800, fontSize: 15 }}>{t.label || t.name}</div>
      <div style={{ fontSize: 12, color: "#a3a3a3", marginBottom: 10 }}>{t.capacity || ""}{t.direction ? ` · ${t.direction === "lower" ? "menos es mejor" : "más es mejor"}` : ""}{t.min_change !== null && t.min_change !== undefined ? ` · cambio mínimo relevante ${t.min_change} ${u}` : ""}</div>
      {s && (<>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          <Kpi l="Actual" v={fmt(s.current, u)} sub={s.current_date} testid={`pp-current-${t.id}`} />
          <Kpi l="Anterior" v={fmt(s.previous, s.previous_unit || u)} sub={s.previous_date} testid={`pp-previous-${t.id}`} />
          {s.best !== null && s.best !== undefined && <Kpi l="Mejor" v={fmt(s.best, u)} sub={s.best_date} testid={`pp-best-${t.id}`} />}
          {s.diff !== undefined && <Kpi l="Diferencia" v={`${s.diff > 0 ? "+" : ""}${s.diff} ${u}`} sub={s.pct !== undefined ? `${s.pct > 0 ? "+" : ""}${s.pct} %` : null} testid={`pp-diff-${t.id}`} />}
        </div>
        {v && <div style={{ fontSize: 12, fontWeight: 700, color: v[1], marginTop: 8 }} data-testid={`pp-verdict-${t.id}`}>{v[0]}{s.verdict === "within_error" ? ` (variación menor que ${t.min_change} ${u})` : ""}</div>}
        {s.warning && <div style={{ fontSize: 12, color: "#facc15", marginTop: 8 }} data-testid={`pp-warning-${t.id}`}>⚠ {s.warning}</div>}
        {p.series?.length > 1 && <Chart p={p} />}
      </>)}
      {p.qualitative_history?.length > 0 && (
        <div style={{ marginTop: 8 }} data-testid={`pp-qual-${t.id}`}>
          {t.qualitative_options?.length > 0 && <div style={{ fontSize: 11, color: "#737373", marginBottom: 4 }}>Escala: {t.qualitative_options.join(" · ")}</div>}
          {p.qualitative_history.map((q, i) => <div key={i} style={{ fontSize: 13, padding: "3px 0" }}><b>{q.date}</b> · {q.value || "—"}{q.text ? ` — ${q.text}` : ""}</div>)}
        </div>
      )}
      {p.assessment_history?.length > 0 && <div style={{ marginTop: 8, fontSize: 12 }}>{p.assessment_history.map((q, i) => <div key={i}>Valoración técnica {q.date}: {[q.value, q.text].filter(Boolean).join(" — ")}</div>)}</div>}
      <div style={{ marginTop: 10 }}>
        {[...p.results].reverse().map((r) => (
          <div key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "6px 0", fontSize: 12 }} data-testid={`pp-res-${r.id}`}>
            <b>{r.date}</b> · {r.final_value !== null && r.final_value !== undefined ? fmt(r.final_value, r.unit || (t.measure_type === "score" || t.result_type === "score" ? "pts" : "")) : r.qualitative || "—"} · <span style={{ color: r.status === "approved" ? "#4ade80" : r.status === "rejected" ? "#f87171" : "#60a5fa" }}>{RES_STATUS[r.status]}</span>
            {r.video_reviewed && <span style={{ color: "#4ade80" }}> · vídeo revisado</span>}
            {r.review_comment && <div style={{ color: "#d4d4d4" }}>Entrenador: {r.review_comment}</div>}
            {(r.coach_comments || []).map((c, i) => <div key={i} style={{ color: "#d4d4d4" }} data-testid={`pp-coach-comment-${r.id}-${i}`}>Entrenador: {c.text}</div>)}
            {admin && (r.internal_comments || []).map((c, i) => <div key={`i${i}`} style={{ color: "#facc15" }} data-testid={`pp-internal-comment-${r.id}-${i}`}>Interno: {c.text}</div>)}
            {r.corrected && <div style={{ color: "#a3a3a3" }}>Resultado corregido{admin && r.corrections?.length ? ` (${r.corrections.length} cambio${r.corrections.length > 1 ? "s" : ""})` : ""}</div>}
            {admin && <Feedback r={r} onSaved={reload} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export const ProgressPanel = ({ items, admin, reload }) => (
  <div data-testid="progress-panel">
    {!items?.length ? <div className="qv-empty" data-testid="progress-empty">Todavía no hay resultados para mostrar la evolución.</div>
      : items.map((p) => <TestCard key={p.template.id} p={p} admin={admin} reload={reload} />)}
  </div>
);
